
import { useState, useEffect, useRef } from 'react';
import { useUserStorage } from "./utils/storageUtils";

export const usePaymentFlow = (resetForm: () => void) => {
  const [showPayment, setShowPayment] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [currentCampaignId, setCurrentCampaignId] = useState<string | null>(null);
  const [canceledPayment, setCanceledPayment] = useState(false);
  const { getItem, setItem, removeItem } = useUserStorage();
  const canceledRef = useRef(false); // Ref to track cancellation across renders
  const cancelInProgressRef = useRef(false); // Additional ref to track active cancellation
  const preventResetRef = useRef(false); // Ref to prevent form reset during cancellation
  const draftIdRef = useRef<string | null>(null); // NEW: Track draft ID across renders

  // NEW: Check if we're working with a draft on component mount
  useEffect(() => {
    const draftId = localStorage.getItem('draftCampaignId');
    if (draftId) {
      console.log("usePaymentFlow: Draft ID detected:", draftId);
      draftIdRef.current = draftId;
    }
  }, []);
  
  // Load campaign ID from user-specific localStorage on component mount
  useEffect(() => {
    const savedCampaignId = getItem('currentCampaignId');
    if (savedCampaignId) {
      setCurrentCampaignId(savedCampaignId);
    }
  }, [getItem]);

  // Save campaign ID to user-specific localStorage when it changes
  useEffect(() => {
    if (currentCampaignId) {
      setItem('currentCampaignId', currentCampaignId);
    } else if (!canceledPayment && !canceledRef.current) {
      // Only remove if not canceled - this prevents clearing during cancellation flow
      removeItem('currentCampaignId');
    }
  }, [currentCampaignId, setItem, removeItem, canceledPayment]);

  // Clean up user-specific localStorage and reset form ONLY when thank you popup is closed
  // and NOT when payment is canceled
  useEffect(() => {
    // Only reset when thank you popup was shown and is now closed
    if (!showThankYou && currentCampaignId && !canceledPayment && !canceledRef.current && !preventResetRef.current) {
      // NEW: Check if we're working with a draft before resetting
      if (draftIdRef.current) {
        console.log("usePaymentFlow: Not resetting form for draft campaign:", draftIdRef.current);
        // Don't reset form for draft campaigns
        removeItem('currentCampaignId');
        setCurrentCampaignId(null);
      } else {
        console.log("usePaymentFlow: Thank you popup closed, resetting form");
        resetForm();
        removeItem('currentCampaignId');
        setCurrentCampaignId(null);
        
        // Also clean up preservation flags from localStorage
        localStorage.removeItem('preservedStep');
        localStorage.removeItem('preserveCampaignId');
        localStorage.removeItem('paymentCanceled'); // Clear payment canceled flag
      }
    }
    
    // Reset the canceledPayment flag when thank you dialog closes
    if (!showThankYou) {
      setCanceledPayment(false);
    }
  }, [showThankYou, currentCampaignId, resetForm, removeItem, canceledPayment]);

  // Update cancellation ref whenever canceledPayment changes
  useEffect(() => {
    canceledRef.current = canceledPayment;
    
    // If cancellation just happened, set a flag in localStorage
    // This flag will be checked by other components
    if (canceledPayment && !cancelInProgressRef.current) {
      console.log("usePaymentFlow: Payment cancellation detected, setting flags");
      cancelInProgressRef.current = true;
      preventResetRef.current = true; // Set flag to prevent form reset
      
      // Set payment cancellation flag with timestamp to prevent race conditions
      localStorage.setItem('paymentCanceled', 'true');
      localStorage.setItem('paymentCanceledAt', Date.now().toString());
      
      // NEW: Store draft ID separately for restoration
      if (draftIdRef.current) {
        localStorage.setItem('canceledDraftId', draftIdRef.current);
      }
      
      // Ensure step is preserved
      const currentStep = localStorage.getItem('currentStep');
      if (currentStep) {
        localStorage.setItem('preservedStep', currentStep);
        localStorage.setItem('preserveCampaignId', 'true');
      }
      
      // Dispatch event for other components to respond to
      document.dispatchEvent(new CustomEvent('payment-cancel-confirmed', { 
        detail: { 
          timestamp: Date.now(),
          isDraft: !!draftIdRef.current,
          draftId: draftIdRef.current
        }
      }));
      
      // Reset the cancellation in progress flag after a delay
      setTimeout(() => {
        cancelInProgressRef.current = false;
        // But keep the preventReset flag active longer
        setTimeout(() => {
          preventResetRef.current = false;
        }, 5000); // Keep prevention active for 5 seconds
      }, 2000); // Give enough time for all handlers to process
    }
  }, [canceledPayment]);

  return {
    showPayment,
    setShowPayment,
    currentCampaignId,
    setCurrentCampaignId,
    showThankYou,
    setShowThankYou,
    canceledPayment,
    setCanceledPayment
  };
};
